// Ported from hotwire-flow (MIT, @hotwirebits/flow) into conek-ui. The engine
// logic is upstream's; identifiers/events/classes follow the conek contract in
// docs/superpowers/specs/2026-09-09-flow-graph-components-design.md §2.
import { Controller } from "@hotwired/stimulus"
import { resolveFlowEngine } from "../../../lib/flow/utils.js"

// Toolbar — zoom controls, fit view, zoom percentage.
export default class extends Controller {
  static targets = ["zoomLevel"]
  static outlets = ["ui--flow"]

  connect() {
    this._onChange = () => this._updateZoomLevel()
    document.addEventListener("ui--flow:viewportchange", this._onChange)
    this._updateZoomLevel()

    // Chrome-insets contract (UX audit): the SSR inline style keeps the 16px
    // baseline; JS lifts the anchored side (bottom for the default
    // bottom-left cluster) by the engine's measured strip inset so the
    // cluster clears the status bar. The base offsets are captured ONCE —
    // re-applies parse nothing, they rewrite base + inset.
    this._baseBottom = this._parsePx(this.element.style.bottom)
    this._baseTop = this._parsePx(this.element.style.top)
    this._applyChromeLift()
    this._onChromeChange = () => this._applyChromeLift()
    this._onResize = () => this._scheduleChromeLift()
    document.addEventListener("ui--flow:chromechange", this._onChromeChange)
    window.addEventListener("resize", this._onResize)
    this._announceChrome()
  }

  disconnect() {
    document.removeEventListener("ui--flow:viewportchange", this._onChange)
    document.removeEventListener("ui--flow:chromechange", this._onChromeChange)
    window.removeEventListener("resize", this._onResize)
    if (this._liftRaf) { cancelAnimationFrame(this._liftRaf); this._liftRaf = null }
    this._announceChrome()
  }

  zoomIn()   { this.uiFlowOutlet?.zoomIn() }
  zoomOut()  { this.uiFlowOutlet?.zoomOut() }
  fitView()  { this.uiFlowOutlet?.fitView() }
  resetView(){ this.uiFlowOutlet?.setViewport({ x: 0, y: 0, zoom: 1 }) }

  _updateZoomLevel() {
    if (this.hasZoomLevelTarget && this.hasUiFlowOutlet) {
      this.zoomLevelTarget.textContent = `${this.uiFlowOutlet.zoomPercentage}%`
    }
  }

  _parsePx(value) {
    const n = parseFloat(value)
    return Number.isFinite(n) ? n : null
  }

  // Rewrite the inline bottom/top the SSR position style baked (see
  // controls_component.rb POSITION_STYLES — the Ruby table stays the no-JS
  // baseline; the engine's insets are the runtime source of truth).
  _applyChromeLift() {
    const insets = resolveFlowEngine(this)?.getChromeInsets?.() || { top: 0, bottom: 0 }
    if (this._baseBottom !== null) this.element.style.bottom = `${this._baseBottom + insets.bottom}px`
    if (this._baseTop !== null) this.element.style.top = `${this._baseTop + insets.top}px`
  }

  _announceChrome() {
    const selector = this.element.getAttribute("data-ui--flow-toolbar-ui--flow-outlet")
    const canvasId = selector && selector.startsWith("#") ? selector.slice(1) : null
    document.dispatchEvent(new CustomEvent("ui--flow:chromechange", { detail: { canvasId } }))
  }

  _scheduleChromeLift() {
    if (this._liftRaf) return
    this._liftRaf = requestAnimationFrame(() => {
      this._liftRaf = null
      this._applyChromeLift()
    })
  }
}
